import React, { useState } from 'react';
import { Loader2, Search, Phone, AlertCircle, History, TrendingUp, TrendingDown, Trophy, Clock, ScanLine } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import LoyaltyQRScanner from '../loyalty/LoyaltyQRScanner';

/**
 * Detailed Loyalty member card for the Add Revenue flow.
 * Shows: name, membership #, phone, current points, AED value,
 * expiring-soon points and pending (awaiting approval) points.
 * Also offers an inline "redeem points" widget linked to the bill.
 */
const LoyaltyMemberCard = ({
  language,
  membershipNumber,
  onMembershipChange,
  onLookup,
  loyaltyMember,
  loading,
  redeemPoints,
  onRedeemChange,
  billAmount,
}) => {
  const t = (ar, en) => (language === 'ar' ? ar : en);
  const [scannerOpen, setScannerOpen] = useState(false);

  return (
    <div>
      <label className="block text-sm font-medium mb-2 flex items-center gap-2">
        <span>{t('رقم عضوية الولاء (اختياري)', 'Loyalty Membership # (optional)')}</span>
        {loyaltyMember && (
          <span
            className="text-xs px-2 py-0.5 rounded-full font-bold"
            style={{ background: loyaltyMember.level.color + '22', color: loyaltyMember.level.color }}
          >
            {language === 'ar' ? loyaltyMember.level.name_ar : loyaltyMember.level.name_en}{' '}
            • {loyaltyMember.points_balance} {t('نقطة', 'pts')}
          </span>
        )}
      </label>

      {/* Lookup row */}
      <div className="flex gap-2">
        <Input
          data-testid="loyalty-membership-input"
          type="text"
          value={membershipNumber}
          onChange={(e) => onMembershipChange(e.target.value)}
          onBlur={(e) => onLookup(e.target.value.trim())}
          placeholder={t('مثال: 100001', 'e.g. 100001')}
          className="bg-secondary/50 border-white/10"
        />
        <Button
          type="button"
          data-testid="loyalty-scan-qr-btn"
          onClick={() => setScannerOpen(true)}
          variant="outline"
          className="border-emerald-500/40 hover:bg-emerald-500/10 px-3"
          title={t('مسح رمز QR للبطاقة', 'Scan loyalty QR')}
        >
          <ScanLine className="w-4 h-4 text-emerald-500" />
        </Button>
        <Button
          type="button"
          data-testid="loyalty-lookup-btn"
          onClick={() => onLookup((membershipNumber || '').trim())}
          disabled={loading || !membershipNumber}
          variant="outline"
          className="border-primary/40 hover:bg-primary/10"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          <span className="ms-1 hidden sm:inline">{t('بحث', 'Lookup')}</span>
        </Button>
      </div>

      <LoyaltyQRScanner
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        isArabic={language === 'ar'}
        onDetected={(num) => {
          setScannerOpen(false);
          onMembershipChange(num);
          onLookup(num);
        }}
      />

      {/* Detail card */}
      {loyaltyMember && (
        <div
          className="mt-2 p-3 rounded-lg border space-y-2 text-xs"
          style={{
            background: `linear-gradient(135deg, ${loyaltyMember.level.color}15, transparent)`,
            borderColor: `${loyaltyMember.level.color}55`,
          }}
          data-testid="loyalty-member-detail-card"
        >
          {/* Status badges (VIP / Welcome back / Recent / Pending) */}
          {(() => {
            const visits = parseInt(loyaltyMember.visits_count || 0);
            const daysAway = loyaltyMember.streak_status?.days_since_last_visit;
            const isVip = visits >= 10;
            const isReturning = typeof daysAway === 'number' && daysAway >= 60;
            const isRecent = loyaltyMember.is_recent;
            const isPending = loyaltyMember.pending_member;
            const graceLeft = loyaltyMember.pending_grace_remaining;
            if (!isVip && !isReturning && !isRecent && !isPending) return null;
            return (
              <div className="flex flex-wrap gap-1.5" data-testid="loyalty-badges">
                {isPending && (() => {
                  const left = typeof graceLeft === 'number' ? graceLeft : 0;
                  let cls = 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300';
                  let pulse = '';
                  let icon = '⏳';
                  if (left <= 0) {
                    cls = 'bg-red-700/30 border-red-700/70 text-red-200';
                    pulse = 'animate-pulse';
                    icon = '⛔';
                  } else if (left < 1) {
                    cls = 'bg-red-500/25 border-red-500/60 text-red-300';
                    pulse = 'animate-pulse';
                    icon = '🔴';
                  } else if (left <= 2) {
                    cls = 'bg-orange-500/20 border-orange-500/55 text-orange-300';
                    pulse = 'animate-pulse';
                    icon = '🟠';
                  } else if (left <= 3) {
                    cls = 'bg-amber-500/20 border-amber-500/55 text-amber-300';
                    icon = '🟡';
                  }
                  return (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${cls} ${pulse}`}
                      data-testid="badge-pending"
                      title={t(
                        `العضوية معلّقة بانتظار اعتماد المدير — متبقي ${left} يوم لتسجيل إيرادات. بعدها يتم رفض كل العمليات حتى يعتمدها المدير.`,
                        `Membership pending admin approval — ${left}d left to add revenues. After that all ops are blocked until admin approves.`
                      )}
                    >
                      {icon}{' '}
                      {left <= 0
                        ? t('انتهت فترة السماح — اطلب الاعتماد', 'Grace expired — request approval')
                        : t(`معلّق • متبقي ${left}d`, `Pending • ${left}d left`)}
                    </span>
                  );
                })()}
                {isRecent && !isPending && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-300 text-[10px] font-bold"
                    data-testid="badge-recent"
                    title={t(
                      'سيُسجَّل ربط هذه العضوية بالإيراد كطلب اعتماد للمدير (7 أيام)',
                      'Linking this new membership will require admin approval within 7 days'
                    )}
                  >
                    🆕 {t(`عضوية جديدة • ${loyaltyMember.days_since_join ?? 0}d`, `New • ${loyaltyMember.days_since_join ?? 0}d`)}
                  </span>
                )}
                {isVip && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-400 text-[10px] font-bold"
                    data-testid="badge-vip"
                  >
                    <Trophy className="w-3 h-3" />
                    {t(`عميل دائم • ${visits} زيارة`, `VIP • ${visits} visits`)}
                  </span>
                )}
                {isReturning && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/15 border border-sky-500/40 text-sky-400 text-[10px] font-bold animate-pulse"
                    data-testid="badge-returning"
                  >
                    <Clock className="w-3 h-3" />
                    {t(`عودة بعد ${daysAway} يوم — رحّب به!`, `Welcome back · ${daysAway}d away`)}
                  </span>
                )}
              </div>
            );
          })()}

          {/* Pending-membership banner: clear notice that points are queued */}
          {loyaltyMember.pending_member && (
            <div
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-[11px] leading-relaxed text-amber-200"
              data-testid="pending-member-notice"
            >
              <div className="font-bold flex items-center gap-1 mb-0.5">
                ⏳ {t('عضوية قيد اعتماد المدير', 'Membership pending admin approval')}
              </div>
              {t(
                'لا يمكن إضافة أو استبدال نقاط الآن. سيتم احتساب نقاط هذا الإيراد تلقائياً فور اعتماد المدير للعضوية.',
                'Points cannot be earned or redeemed yet. They will be credited automatically once the admin approves the membership.'
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            <div>
              <span className="text-muted-foreground">{t('الاسم:', 'Name:')}</span>{' '}
              <span className="font-bold">{loyaltyMember.name}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('رقم العضوية:', 'Membership:')}</span>{' '}
              <span className="font-mono font-bold">#{loyaltyMember.membership_number}</span>
            </div>
            {loyaltyMember.phone && (
              <div className="col-span-2 flex items-center gap-1">
                <Phone className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">{t('الجوال:', 'Phone:')}</span>{' '}
                <a
                  href={`tel:${loyaltyMember.phone}`}
                  className="font-mono font-bold hover:text-primary"
                  dir="ltr"
                >
                  {loyaltyMember.phone}
                </a>
              </div>
            )}
            <div>
              <span className="text-muted-foreground">{t('النقاط الحالية:', 'Current points:')}</span>{' '}
              <span className="font-bold text-emerald-500">
                {loyaltyMember.points_balance} {t('نقطة', 'pts')}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('قيمتها رصيداً:', 'AED value:')}</span>{' '}
              <span className="font-bold text-amber-500">
                {Number(loyaltyMember.redeemable_aed || 0).toFixed(2)} {t('د.إ', 'AED')}
              </span>
            </div>
            {loyaltyMember.expiring_soon_points > 0 && (
              <div className="col-span-2">
                <span className="text-muted-foreground">⚠️ {t('نقاط ستنتهي قريباً:', 'Expiring soon:')}</span>{' '}
                <span className="font-bold text-red-400">
                  {loyaltyMember.expiring_soon_points} {t('نقطة', 'pts')}
                </span>
              </div>
            )}
            {loyaltyMember.pending_points > 0 && (
              <div className="col-span-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 text-yellow-500" />
                <span className="text-muted-foreground">
                  {t('نقاط بانتظار اعتماد المدير:', 'Pending approval:')}
                </span>{' '}
                <span className="font-bold text-yellow-400">
                  {loyaltyMember.pending_points} {t('نقطة', 'pts')}
                </span>
                {loyaltyMember.pending_requests_count > 1 && (
                  <span className="text-[10px] text-muted-foreground">
                    ({loyaltyMember.pending_requests_count} {t('طلب', 'requests')})
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-white/10 pt-2 mt-2 space-y-1">
            {(() => {
              const bill = parseFloat(billAmount) || 0;
              const redeemQty = Math.min(parseInt(redeemPoints) || 0, loyaltyMember.points_balance || 0);
              const discountAed = redeemQty * 0.07;
              const netAmount = Math.max(0, bill - discountAed);
              const newPoints = Math.floor(netAmount);
              const bonusPct = loyaltyMember.level.bonus_pct || 0;
              const bonusPoints = Math.floor((newPoints * bonusPct) / 100);
              return (
                <>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">{t('المبلغ قبل الخصم:', 'Amount before discount:')}</span>
                    <span className="font-mono font-bold">
                      {bill.toFixed(2)} {t('د.إ', 'AED')}
                    </span>
                  </div>
                  {discountAed > 0 && (
                    <div className="flex items-center justify-between text-[11px] text-rose-400">
                      <span>− {t(`خصم (${redeemQty} نقطة)`, `Discount (${redeemQty} pts)`)}:</span>
                      <span className="font-mono font-bold">
                        −{discountAed.toFixed(2)} {t('د.إ', 'AED')}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-[12px] border-t border-white/10 pt-1">
                    <span className="text-muted-foreground font-bold">
                      {t('المبلغ بعد الخصم:', 'Amount after discount:')}
                    </span>
                    <span className="font-mono font-bold text-amber-400">
                      {netAmount.toFixed(2)} {t('د.إ', 'AED')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-emerald-500 pt-1">
                    <span>✓ {t('النقاط المضافة:', 'Points to earn:')}</span>
                    <span className="font-bold">
                      +{newPoints + bonusPoints} {t('نقطة', 'pts')}
                      {bonusPct > 0 && bonusPoints > 0 && (
                        <span className="text-[10px] text-muted-foreground ms-1">
                          ({newPoints} + {bonusPoints} {t('بونص', 'bonus')} {bonusPct}%)
                        </span>
                      )}
                    </span>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Recent transactions */}
          {Array.isArray(loyaltyMember.recent_transactions) &&
            loyaltyMember.recent_transactions.length > 0 && (
              <div
                className="border-t border-white/10 pt-2 mt-2 space-y-1"
                data-testid="loyalty-recent-transactions"
              >
                <div className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
                  <History className="w-3 h-3" />
                  {t('آخر المعاملات على هذه العضوية:', 'Recent activity on this membership:')}
                </div>
                {loyaltyMember.recent_transactions.slice(0, 3).map((tx, idx) => {
                  const isEarn = tx.type === 'earn';
                  const dateStr = (() => {
                    try {
                      return new Date(tx.created_at).toLocaleDateString(
                        language === 'ar' ? 'ar-AE' : 'en-AE',
                        { day: '2-digit', month: 'short' }
                      );
                    } catch {
                      return '';
                    }
                  })();
                  return (
                    <div
                      key={`${tx.created_at}-${idx}`}
                      className={`flex items-center justify-between gap-2 px-2 py-1 rounded text-[11px] ${
                        isEarn ? 'bg-emerald-500/5' : 'bg-rose-500/5'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        {isEarn ? (
                          <TrendingUp className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                        ) : (
                          <TrendingDown className="w-3 h-3 text-rose-500 flex-shrink-0" />
                        )}
                        <span className="truncate">
                          {tx.service_name || (isEarn ? t('كسب', 'earn') : t('استبدال', 'redeem'))}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {tx.amount_aed > 0 && (
                          <span className="text-muted-foreground">
                            {Number(tx.amount_aed).toFixed(0)} {t('د.إ', 'AED')}
                          </span>
                        )}
                        <span className={`font-bold ${isEarn ? 'text-emerald-500' : 'text-rose-500'}`}>
                          {isEarn ? '+' : '−'}
                          {tx.points}
                        </span>
                        {dateStr && <span className="text-muted-foreground">· {dateStr}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          {/* Redeem input */}
          {loyaltyMember.points_balance > 0 && !loyaltyMember.pending_member && (
            <div className="border-t border-white/10 pt-2 mt-2 space-y-1">
              <label className="block text-[11px] text-muted-foreground">
                {t(
                  'استخدام نقاط من رصيد العميل لخصمها من الفاتورة (اختياري):',
                  'Use member points to discount the invoice (optional):'
                )}
              </label>
              <div className="flex gap-2 items-center">
                <Input
                  data-testid="loyalty-redeem-input"
                  type="number"
                  min="0"
                  max={loyaltyMember.points_balance}
                  value={redeemPoints}
                  onChange={(e) => {
                    const v = e.target.value;
                    const n = parseInt(v) || 0;
                    if (n > loyaltyMember.points_balance) {
                      onRedeemChange(String(loyaltyMember.points_balance));
                    } else {
                      onRedeemChange(v);
                    }
                  }}
                  placeholder="0"
                  className="h-8 bg-secondary/50 border-white/10 text-xs flex-1"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 text-xs"
                  data-testid="loyalty-redeem-all-btn"
                  onClick={() => onRedeemChange(String(loyaltyMember.points_balance))}
                >
                  {t('الكل', 'All')} ({loyaltyMember.points_balance})
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 text-xs"
                  data-testid="loyalty-redeem-bill-btn"
                  onClick={() => {
                    const billAed = parseFloat(billAmount) || 0;
                    const maxNeededPts = Math.ceil(billAed / 0.07);
                    const usePts = Math.min(loyaltyMember.points_balance, maxNeededPts);
                    onRedeemChange(String(usePts));
                  }}
                >
                  {t('للفاتورة', 'For bill')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LoyaltyMemberCard;
